import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Cookie, X, Check } from "lucide-react";
import { api } from "../lib/config";

const KEY = "fp_cookie_consent";
export const OPEN_COOKIE_EVENT = "fp:open-cookie-banner";

export function openCookiePreferences() {
  window.dispatchEvent(new CustomEvent(OPEN_COOKIE_EVENT));
}

export default function CookieBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    try {
      const seen = localStorage.getItem(KEY);
      if (!seen) {
        const t = setTimeout(() => setShow(true), 600);
        return () => clearTimeout(t);
      }
    } catch (_) {
      setShow(true);
    }
  }, []);

  useEffect(() => {
    const handler = () => setShow(true);
    window.addEventListener(OPEN_COOKIE_EVENT, handler);
    return () => window.removeEventListener(OPEN_COOKIE_EVENT, handler);
  }, []);

  const record = useCallback(async (choice) => {
    const payload = { accepted: choice === "accept", choice, date: new Date().toISOString() };
    localStorage.setItem(KEY, JSON.stringify(payload));
    setShow(false);
    try {
      await api.post("/consent", { consent_type: "cookie", choice });
    } catch (_) {}
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="cookie-wrap"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[59] pointer-events-none"
          data-testid="cookie-banner-wrap"
        >
          {/* Backdrop visivo (non blocca i click sulla pagina) */}
          <div
            className="absolute inset-0 bg-black/30 pointer-events-none"
          />
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", damping: 22 }}
            className="absolute bottom-4 left-4 right-4 md:bottom-6 md:left-6 md:right-6 md:max-w-2xl md:mx-auto pointer-events-auto"
            data-testid="cookie-banner"
            role="dialog"
            aria-label="Informativa cookie"
          >
            <div className="relative p-5 md:p-7 rounded-2xl border border-white/10 bg-fp-surface/95 backdrop-blur-xl shadow-2xl tracing-border">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-fp-cyan/10 border border-fp-cyan/30 text-fp-cyan flex items-center justify-center flex-shrink-0">
                  <Cookie size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-bold text-base md:text-lg">La tua privacy è importante</h3>
                  <p className="mt-2 text-white/70 text-sm leading-relaxed">
                    Utilizziamo esclusivamente <strong className="text-white">cookie tecnici</strong> necessari al funzionamento del sito (autenticazione admin, memorizzazione della tua scelta sui cookie).
                    Non utilizziamo cookie di profilazione, marketing, analytics o tracciamento di terze parti.
                    Per i dettagli consulta la{" "}
                    <Link to="/cookie" className="text-fp-cyan hover:underline">Cookie Policy</Link> e la{" "}
                    <Link to="/privacy" className="text-fp-cyan hover:underline">Privacy Policy</Link>.
                  </p>
                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      onClick={() => record("accept")}
                      className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-fp-cyan text-fp-bg font-heading font-bold text-xs tracking-wider uppercase hover:shadow-[0_0_20px_rgba(0,229,255,0.5)] hover:-translate-y-0.5 transition-all"
                      data-testid="cookie-accept"
                    >
                      <Check size={14} /> Accetta tutti
                    </button>
                    <button
                      onClick={() => record("reject")}
                      className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/10 text-white border border-white/20 font-heading font-bold text-xs tracking-wider uppercase hover:bg-white/15 hover:border-white/30 hover:-translate-y-0.5 transition-all"
                      data-testid="cookie-reject"
                    >
                      <X size={14} /> Rifiuta tutti
                    </button>
                  </div>
                  <div className="mt-3 text-[11px] text-white/40">
                    Le tue scelte sono registrate e modificabili in qualsiasi momento dal link "Preferenze Cookie" nel footer.
                  </div>
                </div>
                <button
                  onClick={() => record("reject")}
                  className="text-white/40 hover:text-white transition-colors p-1"
                  aria-label="Chiudi"
                  data-testid="cookie-close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
